import React, { useState, useEffect, useMemo } from "react";
import Spinner from "../Spinner/Spinner"; // Make sure you have this component for loading indication
import useCRUD from "../../hooks/useCRUD";
import AddOrEditParticipantModal from "../EventDashboard/AddorEditParticipantModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMobileScreen,
  faMapPin,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import GeocodePromptModal from "../Elements/GeocodePromptModal";
import NotificationPauseModal from "./NotificationPauseModal";
import {
  geocodeParticipant,
  fetchAccountSettings,
  hasParticipantCoords,
} from "../../utils/geocode";
import { formatPhoneNumber } from "../../utils/phoneNumberUtils";

const Drivers = ({ event, drivers = [], refreshData, groupId }) => {
  const [isLoading, setIsLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [pauseDriver, setPauseDriver] = useState(null);
  const [error, setError] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "ascending",
  });

  const { addItem, updateItem, deleteItem } = useCRUD("/api/participants");
  console.log("drivers", drivers);

  const sortedDrivers = useMemo(() => {
    let sortableItems = [...drivers];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [drivers, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleAddNewDriverClick = () => {
    setIsModalOpen(true);
    setModalData(null); // Initialize data for new driver
  };

  const handleEditClick = (driver) => {
    setIsModalOpen(true);
    setModalData({ ...driver, isDriver: true }); // Set modal for editing a driver
  };

  const currentNotificationPause = (driver) =>
    driver.activeNotificationPause || driver.upcomingNotificationPause;

  const handleRemoveNotificationPause = async (driver) => {
    const pause = currentNotificationPause(driver);
    if (!pause) return;

    if (!window.confirm(`Remove RSVP pause for ${driver.name}?`)) return;

    try {
      setIsLoading(true);
      const token = localStorage.getItem("userToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/participant-notification-pauses/${pause.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) throw new Error("Could not remove RSVP pause.");
      refreshData();
    } catch (error) {
      alert(error.message || "Could not remove RSVP pause.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      setIsLoading(true);
      setError(""); // Reset error message on new save attempt

      let response; // This will store the response from your save functions

      if (modalData) {
        // Editing an existing participant
        response = await updateItem(modalData.id, data);
      } else {
        // Adding a new participant
        data.participantGroupId = groupId;
        response = await addItem(data);
      }

      // Check the response to ensure it was successful
      // This assumes your addItem/updateItem functions return some success indicator
      // Adjust the following line according to your actual API response structure
      if (response && response.data) {
        console.log("Participant saved successfully:", data);
        // Optionally, refresh your participants list here
      } else {
        // Handle unsuccessful save
        // Adjust the following line according to your actual API response structure
        throw new Error(
          response.message || "An error occurred while saving the participant.",
        );
      }
    } catch (error) {
      console.error("Error saving participant:", error);
      setError(error.message || "Error saving participant. Please try again.");
    } finally {
      setIsModalOpen(false); // Close the modal after attempting save
      refreshData(); // Refresh data regardless of success/failure
      setIsLoading(false);
    }
  };

  const handleDeleteClick = async (driver) => {
    try {
      setIsLoading(true);
      await deleteItem(driver.id);
      console.log("Participant deleted successfully:", driver);
      // Optionally, refresh your participants list here
    } catch (error) {
      console.error("Error deleting participant:", error);
    } finally {
      refreshData();
      setIsLoading(false);
    }
  };

  const apiBaseUrl = process.env.REACT_APP_API_URL;

  const handleInviteToApp = async (driver) => {
    if (!window.confirm(`Send an app download invite SMS to ${driver.name}?`)) {
      return;
    }
    try {
      setIsLoading(true);
      const token = localStorage.getItem("userToken");
      const response = await fetch(
        `${apiBaseUrl}/api/participants/${driver.id}/invite-to-app`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.message || "Failed to send invite.");
      }
      alert("Invite sent.");
    } catch (err) {
      console.error("Error sending invite:", err);
      alert(err.message || "Failed to send invite.");
    } finally {
      setIsLoading(false);
    }
  };

  const mobileDriverCount = drivers.filter((d) => d.hasMobileApp).length;

  const [geocodeModal, setGeocodeModal] = useState({
    open: false,
    participant: null,
  });

  const runGeocodeForDriver = async (driver, extras = {}) => {
    try {
      setIsLoading(true);
      await geocodeParticipant(driver.id, extras);
      refreshData();
    } catch (err) {
      console.error("Geocode failed:", err);
      alert(err.message || "Failed to fetch GPS coordinates.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFillGps = async (driver) => {
    try {
      const account = await fetchAccountSettings();
      const hasBase = !!(
        account?.baseCity ||
        account?.baseState ||
        account?.baseCountry
      );
      if (hasBase) {
        await runGeocodeForDriver(driver);
      } else {
        setGeocodeModal({ open: true, participant: driver });
      }
    } catch (err) {
      console.error(err);
      setGeocodeModal({ open: true, participant: driver });
    }
  };

  const handleGeocodeModalSubmit = async ({ city, state, country }) => {
    const driver = geocodeModal.participant;
    setGeocodeModal({ open: false, participant: null });
    if (driver) {
      await runGeocodeForDriver(driver, { city, state, country });
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="card rounded-xl border border-gray-100 bg-white shadow-lg">
      {isLoading && <Spinner />}

      {error && <div className="text-red-500">{error}</div>}

      <div className="card-body">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#004a7e]">
              Group drivers
            </p>
            <h3 className="text-xl font-black text-gray-900">
              {drivers.length} Drivers
            </h3>
            {drivers.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                <FontAwesomeIcon icon={faMobileScreen} className="mr-1" />
                {mobileDriverCount} on mobile app
              </p>
            )}
          </div>
          <button
            className="rounded-lg bg-[#004a7e] px-3 py-2 text-sm font-bold text-white hover:bg-[#003a64]"
            onClick={handleAddNewDriverClick}
          >
            + Add New Driver
          </button>
        </div>

        {isModalOpen && (
          <AddOrEditParticipantModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSave={handleSave}
            initialData={modalData}
            isDriver={true}
            event={event}
          />
        )}

        {sortedDrivers.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-gray-100">
            <table className="table w-full">
              <thead>
                <tr>
                  <th onClick={() => requestSort("name")}>
                    Driver{" "}
                    {sortConfig.key === "name" &&
                      (sortConfig.direction === "ascending" ? "▲" : "▼")}
                  </th>
                  <th className="hidden md:table-cell"> Phone</th>
                  <th onClick={() => requestSort("canTake")}>
                    Can Take{" "}
                    {sortConfig.key === "canTake" &&
                      (sortConfig.direction === "ascending" ? "▲" : "▼")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedDrivers.map((driver) => (
                  <tr key={driver.id}>
                    <td>
                      <div className="font-bold">
                        {driver.name}{" "}
                        {driver.hasMobileApp && (
                          <span
                            className="ml-2 bg-green-600 text-white py-1 px-2 text-xs rounded"
                            title="Using mobile app"
                          >
                            <FontAwesomeIcon icon={faMobileScreen} /> Mobile
                          </span>
                        )}
                        {driver.pauseNotifications && (
                          <span className="ml-2 bg-red-500 text-white py-1 px-2 text-xs rounded">
                            Paused
                          </span>
                        )}
                        {driver.activeNotificationPause && (
                          <span className="ml-2 bg-orange-500 text-white py-1 px-2 text-xs rounded">
                            RSVP paused until{" "}
                            {driver.activeNotificationPause.endsAt}
                          </span>
                        )}
                        {driver.upcomingNotificationPause && (
                          <span className="ml-2 bg-orange-100 text-orange-700 py-1 px-2 text-xs rounded">
                            Pause starts{" "}
                            {driver.upcomingNotificationPause.startsAt}
                          </span>
                        )}
                        {hasParticipantCoords(driver) && (
                          <span
                            className="ml-2 text-green-600"
                            title={
                              driver.pickupGeocodedAddress ||
                              "GPS coordinates saved"
                            }
                          >
                            <FontAwesomeIcon icon={faMapPin} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="hidden md:table-cell">
                      <div className="text-gray-500">
                        {formatPhoneNumber(driver.phone)}
                      </div>
                    </td>
                    <td>{driver.canTake}</td>
                    <td>
                      <div className="dropdown dropdown-end">
                        <button
                          tabIndex="0"
                          className="btn btn-ghost btn-xs m-1"
                        >
                          ...
                        </button>
                        <ul
                          tabIndex="0"
                          className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52"
                        >
                          <li>
                            <a
                              href="#"
                              onClick={() => handleEditClick(driver)}
                              className="justify-between"
                            >
                              Edit
                            </a>
                          </li>
                          {driver.rideLocation && (
                            <li>
                              <button onClick={() => handleFillGps(driver)}>
                                <FontAwesomeIcon icon={faLocationDot} />
                                {hasParticipantCoords(driver)
                                  ? " Refresh GPS"
                                  : " Fill in GPS"}
                              </button>
                            </li>
                          )}
                          {!driver.hasMobileApp && (
                            <li>
                              <button onClick={() => handleInviteToApp(driver)}>
                                <FontAwesomeIcon icon={faMobileScreen} /> Invite
                                to App
                              </button>
                            </li>
                          )}
                          <li>
                            <button onClick={() => setPauseDriver(driver)}>
                              Pause RSVP texts
                            </button>
                          </li>
                          {currentNotificationPause(driver) && (
                            <li>
                              <button
                                onClick={() =>
                                  handleRemoveNotificationPause(driver)
                                }
                              >
                                Remove RSVP pause
                              </button>
                            </li>
                          )}
                          <li>
                            <button onClick={() => handleDeleteClick(driver)}>
                              Delete
                            </button>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="rounded-lg bg-gray-50 p-5 text-center text-gray-600">
            No drivers to display.
          </p>
        )}
      </div>

      <GeocodePromptModal
        isOpen={geocodeModal.open}
        onClose={() => setGeocodeModal({ open: false, participant: null })}
        onSubmit={handleGeocodeModalSubmit}
        title={`Fill in GPS for ${geocodeModal.participant?.name || "driver"}`}
        description="Your account doesn't have a default city/state/country yet. Provide it here so we can resolve partial addresses. You can save it permanently in Settings → Default Location."
      />
      <NotificationPauseModal
        participant={pauseDriver}
        onClose={() => setPauseDriver(null)}
        onSaved={refreshData}
      />
    </div>
  );
};

export default Drivers;
